import {Badge, Container, Nav, Navbar, NavDropdown} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';
import Logo from './ux/Logo';
import {useAuthenticated} from '@utils/hooks/common';
import {useGameStyleEnabled} from '@utils/hooks/useGameStyleEnabled';
import {StatusBar} from './StatusBar';
import {ArrowLeft, LightningChargeFill} from '@goproblems/ui/icons';
import {Link, useLocation} from 'react-router';
import {authSlice} from '@reducers';
import {useDispatch} from 'react-redux';
import {useHeaderNavigation} from './hooks/useHeaderNavigation';

export const Header = () => {
    const {isAuthenticated, user} = useAuthenticated();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const currentRoute = location.pathname;
    const isProblemFilter =
        currentRoute === '/problems/filter' ||
        (currentRoute === '/problems' &&
            new URLSearchParams(location.search).get('filters') === '1');
    const {isGameStyleEnabled} = useGameStyleEnabled();
    const forceDarkMobileLogo = currentRoute === '/' && isGameStyleEnabled;
    const {handleAutolevelClick, handleTrainingClick} = useHeaderNavigation();

    return (
        <Navbar
            collapseOnSelect
            expand="md"
            className="border-b border-neutral-200 shadow-sm dark:border-[var(--ui-border)]">
            <Container
                fluid
                className="flex flex-wrap items-center justify-between md:flex-nowrap">
                <Logo
                    className="mr-4"
                    forceDarkMobile={forceDarkMobileLogo}
                />
                <div className="flex flex-1 justify-center md:hidden">
                    <StatusBar />
                </div>
                <Navbar.Toggle aria-controls="navbar-nav" />
                <Navbar.Collapse id="navbar-nav">
                    <Nav className="w-full items-center md:flex-nowrap">
                        <NavDropdown
                            data-qa={'solve-dropdown'}
                            title={t('navbar.solve')}>
                            <NavDropdown.Item
                                onClick={handleAutolevelClick}
                                data-qa="next-problem">
                                {t('navbar_solve.go_to_problems_at_your_level')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/time-trials">
                                {t('navbar_solve.time_trial')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/rush">
                                {t('navbar_solve.rush')}
                            </NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown title={t('navbar.discover')}>
                            <NavDropdown.Item
                                as={Link}
                                to="/problems/recommendations">
                                {t('navbar_discover.recommendations')}
                            </NavDropdown.Item>
                            <NavDropdown.Item onClick={handleTrainingClick}>
                                {t('navbar_discover.training')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/problems/practice">
                                {t('navbar_discover.practice')}
                            </NavDropdown.Item>
                            <NavDropdown.Divider />
                            <NavDropdown.Item as={Link} to="/problems">
                                {t('navbar_discover.all_problems')}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/problems?filters=1">
                                {t('navbar_discover.filter_problems')}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/collections">
                                {t('navbar_discover.problem_collections')}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/problems/search/pattern">
                                {t('navbar_discover.pattern_search')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/suggestions">
                                {t('navbar_discover.problem_suggestions')}
                            </NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown title={t('navbar.social')}>
                            <NavDropdown.Item as={Link} to="/dashboard/friends">
                                {t('navbar_social.friends')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/user-search">
                                {t('navbar_social.users')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/groups">
                                {t('navbar_social.groups')}
                            </NavDropdown.Item>
                            <NavDropdown.Item href="https://discord.gg/ThjuHPYU9E">
                                {t('navbar_social.discord')}
                            </NavDropdown.Item>
                            <NavDropdown.Item to="/leaderboard" as={Link}>
                                {t('navbar_social.leaderboard')}
                            </NavDropdown.Item>
                            <NavDropdown.Item to="/daily-leaderboard" as={Link}>
                                {t('navbar_social.daily_leaderboard')}
                            </NavDropdown.Item>
                            <NavDropdown.Item to="/potd/streaks" as={Link}>
                                {t('daily_streaks.title')}
                            </NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown title={t('navbar.site')}>
                            <NavDropdown.Item as={Link} to="/article/info">
                                {t('navbar_site.info')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/problems/create">
                                {t('navbar_site.add_problem')}
                            </NavDropdown.Item>
                            {isAuthenticated && user?.isEditor && (
                                <NavDropdown.Item
                                    as={Link}
                                    to="/invasions/create">
                                    {t('navbar_site.add_invasion_problem')}
                                </NavDropdown.Item>
                            )}
                            <NavDropdown.Item
                                as={Link}
                                to="/tournaments/create">
                                {t('navbar_site.create_tournament')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/problems/curate">
                                {t('navbar_site.curate_new_problem')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/statistics">
                                {t('navbar_site.statistics')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/download/">
                                {t('navbar_site.download')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/rss">
                                {t('navbar_site.rss')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/release-notes">
                                {t('release_notes.title')}
                            </NavDropdown.Item>
                            <NavDropdown.Item as={Link} to="/sitemap">
                                {t('navbar_site.sitemap')}
                            </NavDropdown.Item>
                        </NavDropdown>
                        <NavDropdown
                            title={t('navbar.problems')}
                            data-qa={'problem-dropdown'}>
                            <NavDropdown.Item
                                as={Link}
                                to="/problems"
                                className={`dropdown-item ${currentRoute === '/problems' && !isProblemFilter ? 'active' : ''}`}>
                                {t('side_menu.categories.all_problems')}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/problems?filters=1"
                                className={`dropdown-item ${isProblemFilter ? 'active' : ''}`}>
                                {t('side_menu.categories.filter_problems')}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                data-qa="app_problem_search_text"
                                as={Link}
                                to="/problems/search"
                                className={`dropdown-item ${currentRoute === '/problems/search' ? 'active' : ''}`}>
                                {t(
                                    'side_menu.categories.find_problems_by_text'
                                )}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/problems/search/pattern"
                                className={`dropdown-item ${currentRoute === '/problems/search/pattern' ? 'active' : ''}`}>
                                {t(
                                    'side_menu.categories.find_problems_by_pattern'
                                )}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/problems/flagged"
                                className={`dropdown-item ${currentRoute === '/problems/flagged' ? 'active' : ''}`}>
                                {t('side_menu.categories.flagged_problems')}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/comments/recent"
                                className={`dropdown-item ${currentRoute === '/comments/recent' ? 'active' : ''}`}>
                                {t('side_menu.categories.recent_comments')}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/collections"
                                className={`dropdown-item ${currentRoute === '/collections' ? 'active' : ''}`}>
                                {t('side_menu.categories.collections')}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/problems/recommendations"
                                className={`dropdown-item ${currentRoute === '/problems/recommendations' ? 'active' : ''}`}>
                                {t('side_menu.categories.recommendations')}
                            </NavDropdown.Item>
                            <NavDropdown.Item
                                as={Link}
                                to="/collections/progress"
                                className={`dropdown-item ${currentRoute === '/collections/progress' ? 'active' : ''}`}>
                                {t('side_menu.categories.collections_progress')}
                            </NavDropdown.Item>
                            {isAuthenticated && (
                                <NavDropdown.Item
                                    as={Link}
                                    to="/srs"
                                    className={`dropdown-item ${currentRoute === '/srs' ? 'active' : ''}`}>
                                    {t('navbar_discover.training')}
                                </NavDropdown.Item>
                            )}
                            {isAuthenticated && (
                                <NavDropdown.Item
                                    as={Link}
                                    to="/problems/practice"
                                    className={`dropdown-item ${currentRoute === '/problems/practice' ? 'active' : ''}`}>
                                    {t('side_menu.categories.practice')}
                                </NavDropdown.Item>
                            )}
                            {/* {isAuthenticated && user?.isAdmin && (
                                <NavDropdown.Item
                                    href="/invasions"
                                    className={`dropdown-item ${currentRoute === '/invasions' ? 'active' : ''}`}>
                                    {t(
                                        'side_menu.categories.invasion_problems'
                                    )}
                                </NavDropdown.Item>
                            )} */}
                            <div className="dropdown-divider"></div>
                            <NavDropdown.Item
                                as={Link}
                                to="/time-trials"
                                className="dropdown-item">
                                {t('side_menu.other.time_trial')}
                            </NavDropdown.Item>
                            {isAuthenticated && (
                                <NavDropdown.Item
                                    as={Link}
                                    to="/levels"
                                    className="dropdown-item">
                                    {t('side_menu.other.level_progress')}
                                </NavDropdown.Item>
                            )}
                        </NavDropdown>
                        <div className="hidden md:flex md:flex-1 md:justify-center gh-navbar-status">
                            <StatusBar />
                        </div>
                        {isAuthenticated && user ? (
                            <NavDropdown
                                data-qa="auth-dropdown"
                                title={
                                    <span className="inline-flex items-center gap-1">
                                        <span className="gh-navbar-username">
                                            {user.name}
                                        </span>
                                        {user.pro && (
                                            <Badge
                                                appearance="solid"
                                                bg="success"
                                                className="uppercase">
                                                {t('navbar.pro')}
                                            </Badge>
                                        )}
                                    </span>
                                }
                                align={'end'}>
                                <NavDropdown.Item as={Link} to="/dashboard">
                                    {t('navbar_profile.dashboard')}
                                </NavDropdown.Item>
                                <NavDropdown.Item
                                    as={Link}
                                    to="/settings/profile">
                                    {t('navbar_profile.settings')}
                                </NavDropdown.Item>
                                <NavDropdown.Item
                                    as={Link}
                                    to="/settings/board-theme">
                                    {t('navbar_profile.board_theme')}
                                </NavDropdown.Item>
                                <NavDropdown.Item
                                    as={Link}
                                    to="/dashboard/friends">
                                    {t('navbar_profile.friends')}
                                </NavDropdown.Item>
                                <NavDropdown.Item
                                    as={Link}
                                    to={`/users/${user.name}/achievements`}>
                                    {t('navbar_profile.achievements')}
                                </NavDropdown.Item>
                                <NavDropdown.Item
                                    as={Link}
                                    to="/dashboard/friendship-requests">
                                    {t('navbar_profile.friendship_requests')}
                                </NavDropdown.Item>
                                <NavDropdown.Item>
                                    <Link
                                        to={`/users/${user.name}`}
                                        state={{userId: user.id}}>
                                        {t('navbar_profile.public_profile')}
                                    </Link>
                                </NavDropdown.Item>
                                {(user.isEditor ||
                                    user.isTranslator ||
                                    user.isAdmin) && (
                                    <>
                                        <NavDropdown.Divider />
                                        <NavDropdown.Item href="/admin/">
                                            {t(
                                                'navbar_profile.site_management'
                                            )}
                                        </NavDropdown.Item>
                                    </>
                                )}
                                {!user.pro && (
                                    <>
                                        <NavDropdown.Divider />
                                        <NavDropdown.Item
                                            as={Link}
                                            to="/get-pro"
                                            // old
                                            className="fst-italic d-flex align-items-center">
                                            <LightningChargeFill className="pro-icon" />
                                            &nbsp;{t('navbar_profile.get_pro')}
                                        </NavDropdown.Item>
                                    </>
                                )}
                                {user.isImpersonator && (
                                    <>
                                        <NavDropdown.Divider />
                                        <NavDropdown.Item
                                            href="/auth/impersonate?_switch_user=_exit"
                                            className="d-flex align-items-center">
                                            <ArrowLeft />
                                            &nbsp;
                                            {t(
                                                'navbar_profile.exit_impersonation'
                                            )}
                                        </NavDropdown.Item>
                                    </>
                                )}
                                <NavDropdown.Divider />
                                <NavDropdown.Item
                                    data-qa="auth-logout"
                                    // as={Link}
                                    // to="/"
                                    onClick={() =>
                                        dispatch(authSlice.actions.logout())
                                    }>
                                    {t('navbar_profile.logout')}
                                </NavDropdown.Item>
                            </NavDropdown>
                        ) : (
                            <div
                                className="flex gap-2 gh-navbar-auth"
                                style={{marginTop: 1, marginBottom: 1}}>
                                <Link
                                    to="/auth/login"
                                    className="btn btn-outline-primary"
                                    data-qa="login">
                                    {t('navbar_anonymous.sign_in')}
                                </Link>
                                <Link
                                    to="/auth/register"
                                    className="btn btn-primary">
                                    {t('navbar_anonymous.sign_up')}
                                </Link>
                            </div>
                        )}
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
};
